import { Steps } from 'antd'
import { observer } from 'mobx-react-lite'
import { lazy, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { Margin, Padding } from 'components/ui'
import { Button, Flex, Title } from 'components/uiKit'
import { getOrderItemRoute } from 'utils/helpers/routes/getRoutes'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'

import { OrderKeys } from '../../data'
import { OrderStatus } from '../../types'
import { ActivateOrderForm } from './ActivateOrderForm/ActivateOrderForm'
import { OrderCCTVForm } from './ActivateOrderForm/OrderCCTVForm/OrderCCTVForm'

import s from './ActivateOrderPage.module.scss'

const stepTitles = [
  {
    title: 'Проверка видеонаблюдения',
  },
  {
    title: 'Заявка на активацию',
  },
]

const steps: Map<number, JSX.Element> = new Map([
  [1, <OrderCCTVForm key={1} />],
  [2, <ActivateOrderForm key={2} />],
])

const getKey = (key: string) => `${OrderKeys.activateOrderPage}.${key}`

const ActivateOrderPage = observer(() => {
  const { activateOrderUiStore: uiStore, viewOrderUiStore } = useStore()

  const { orderId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const items = stepTitles.map((item) => ({ key: item.title, title: t(getKey(item.title)) }))

  useAsyncEffect(async () => {
    if (orderId) {
      if (viewOrderUiStore.orderList.length === 0) {
        await viewOrderUiStore.getOrderList()
      }

      const order = viewOrderUiStore.getOrder(+orderId)

      if (order?.id) {
        void uiStore.cctv.getCCTVInfo(order.id)

        viewOrderUiStore.setSelectedOrder(order)

        if (order.status !== OrderStatus.remarkFromActivate && order.status !== OrderStatus.accepted) {
          navigate(getOrderItemRoute(+orderId))
        }

        if (order.status === OrderStatus.remarkFromActivate) {
          void uiStore.init()
        }
      }
    }
  }, [orderId])

  useEffect(() => {
    return () => {
      uiStore.reset()
    }
  }, [])

  return (
    <ContainerLK>
      <Padding py={32}>
        <Flex className={s.wrap} vertical gap={32}>
          <Flex gap={16} align="center">
            <Button
              shape="circle"
              icon={<ArrowLeftOutlined />}
              onClick={() => orderId && navigate(getOrderItemRoute(+orderId))}
            />

            <Title>{t(getKey('Активация ПВЗ'))}</Title>
          </Flex>

          <Margin mb={16}>
            <Steps current={uiStore.selectedStep - 1} items={items} />
          </Margin>

          <Flex vertical gap={60} style={{ maxWidth: 600 }}>
            {steps.get(uiStore.selectedStep)}
          </Flex>
        </Flex>
      </Padding>
    </ContainerLK>
  )
})

export default ActivateOrderPage

export const ActivateOrderPageAsync = lazy(() => import('./ActivateOrderPage'))
